import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Gift, Loader2, PauseCircle, PlayCircle, Package, CreditCard, Filter } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { AppLayout } from "@/components/AppLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { useAuth } from "@/context/AuthContext";

const SOURCE_LABEL = { stripe: "Stripe", bundle: "Bundle", manual_comp: "Comped" };
const STATUS_STYLE = {
  active: "border-emerald-500/30 bg-emerald-500/10 text-emerald-600 dark:text-emerald-400",
  past_due: "border-amber-500/30 bg-amber-500/10 text-amber-600 dark:text-amber-400",
  paused: "border-amber-500/30 bg-amber-500/10 text-amber-600 dark:text-amber-400",
  inactive: "border-border bg-muted text-muted-foreground",
};

function SourceCell({ row }) {
  if (row.status === "inactive") return <span className="text-muted-foreground">—</span>;
  const Icon = row.source === "stripe" ? CreditCard : row.source === "bundle" ? Package : Gift;
  return (
    <span className="flex items-center gap-1.5">
      <Icon className="h-3.5 w-3.5 text-muted-foreground" />
      {row.source === "bundle" && row.bundle_name ? `Included with ${row.bundle_name}` : SOURCE_LABEL[row.source] || row.source}
    </span>
  );
}

export default function AddOns() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [source, setSource] = useState("all");
  const [status, setStatus] = useState("all");
  const [busy, setBusy] = useState("");
  const [comp, setComp] = useState(null);     // { row, tier, source, bundle_name, reason, ends_at }
  const [end, setEnd] = useState(null);       // { row, confirm, reason }
  const [attr, setAttr] = useState({});       // WO8 revenue tracking, site_id -> row

  const loadAttr = async () => {
    try {
      const { data } = await api.get("/admin/attribution");
      setAttr(Object.fromEntries(data.rows.map((r) => [r.site_id, r])));
    } catch { /* non-blocking extra column */ }
  };

  const toggleAttr = async (row, on) => {
    setBusy("a" + row.site_id);
    try {
      await api.post(`/admin/attribution/${row.site_id}/${on ? "comp" : "end"}`,
        on ? { source: "manual_comp", reason: "switched on from the add-on desk" } : {});
      toast.success(on ? "Revenue tracking is on." : "Revenue tracking is off.");
      loadAttr();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not change revenue tracking");
    } finally {
      setBusy("");
    }
  };

  const load = async () => {
    const q = new URLSearchParams();
    if (source !== "all") q.set("source", source);
    if (status !== "all") q.set("status", status);
    try {
      const { data } = await api.get(`/admin/entitlements?${q.toString()}`);
      setData(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not load add-ons");
    }
  };
  useEffect(() => { load(); loadAttr(); }, [source, status]);

  const tiers = useMemo(() => data?.tiers || [], [data]);

  const saveComp = async () => {
    setBusy("comp");
    try {
      await api.post(`/admin/entitlements/${comp.row.site_id}/comp`, {
        tier: comp.tier,
        source: comp.source,
        bundle_name: comp.bundle_name || null,
        reason: comp.reason || null,
        ends_at: comp.ends_at || null,
      });
      toast.success(`${comp.row.url} is now on ${tiers.find((t) => t.key === comp.tier)?.label}`);
      setComp(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not save the comp");
    } finally {
      setBusy("");
    }
  };

  const confirmEnd = async () => {
    setBusy("end");
    try {
      const { data } = await api.post(`/admin/entitlements/${end.row.site_id}/end`, {
        confirm: end.confirm, reason: end.reason || null,
      });
      toast.success(`Paused. Connections are kept until ${new Date(data.paused_until).toLocaleDateString()}.`);
      setEnd(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not turn the add-on off");
    } finally {
      setBusy("");
    }
  };

  const resume = async (row) => {
    setBusy("r" + row.site_id);
    try {
      await api.post(`/admin/entitlements/${row.site_id}/resume`);
      toast.success("Back on.");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not resume");
    } finally {
      setBusy("");
    }
  };

  if (user && !["owner", "admin"].includes(user.role)) {
    return (
      <AppLayout>
        <p className="text-sm text-muted-foreground" data-testid="addons-forbidden">
          Add-ons and billing are managed by your account owner.
        </p>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="mb-8">
        <h1 className="font-display text-4xl font-semibold tracking-tight sm:text-5xl">Add-ons</h1>
        <p className="mt-2 max-w-2xl text-base text-muted-foreground">
          Who is paying, who is included in a package, and who is comped. Turning an add-on off pauses it for
          30 days first — connected accounts are never dropped on the spot.
        </p>
      </div>

      <div className="mb-5 flex flex-wrap items-center gap-2">
        <Filter className="h-4 w-4 text-muted-foreground" />
        <Select value={source} onValueChange={setSource}>
          <SelectTrigger className="w-[190px]" data-testid="filter-source"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Every source</SelectItem>
            <SelectItem value="stripe">Stripe</SelectItem>
            <SelectItem value="bundle">Bundle</SelectItem>
            <SelectItem value="manual_comp">Comped</SelectItem>
          </SelectContent>
        </Select>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-[170px]" data-testid="filter-status"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Any status</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="past_due">Payment issue</SelectItem>
            <SelectItem value="paused">Paused</SelectItem>
            <SelectItem value="inactive">No add-on</SelectItem>
          </SelectContent>
        </Select>
        {data?.counts && (
          <div className="flex flex-wrap gap-1.5" data-testid="addon-counts">
            {Object.entries(data.counts).map(([k, v]) => (
              <Badge key={k} variant="outline">{k.replace("_", " ")}: {v}</Badge>
            ))}
          </div>
        )}
      </div>

      {!data ? (
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      ) : (
        <div className="overflow-x-auto rounded-lg border border-border">
          <table className="w-full text-sm" data-testid="addons-table">
            <thead className="bg-muted/50 text-xs uppercase tracking-widest text-muted-foreground">
              <tr>
                <th className="px-4 py-3 text-left font-semibold">Site</th>
                <th className="px-4 py-3 text-left font-semibold">Tier</th>
                <th className="px-4 py-3 text-left font-semibold">Source</th>
                <th className="px-4 py-3 text-left font-semibold">Ends</th>
                <th className="px-4 py-3 text-left font-semibold">Status</th>
                <th className="px-4 py-3 text-left font-semibold">Revenue tracking</th>
                <th className="px-4 py-3 text-right font-semibold">Manage</th>
              </tr>
            </thead>
            <tbody>
              {data.rows.map((row) => (
                <tr key={row.site_id} className="border-t border-border" data-testid={`addon-row-${row.site_id}`}>
                  <td className="px-4 py-3">
                    <p className="font-medium">{row.business_name || row.url}</p>
                    <p className="text-xs text-muted-foreground">{row.url} · {row.connections} connection(s)</p>
                  </td>
                  <td className="px-4 py-3" data-testid={`addon-tier-${row.site_id}`}>{row.label || "—"}</td>
                  <td className="px-4 py-3"><SourceCell row={row} /></td>
                  <td className="px-4 py-3 text-muted-foreground">
                    {row.ends_at ? new Date(row.ends_at).toLocaleDateString() : "—"}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`rounded-full border px-2 py-0.5 text-xs ${STATUS_STYLE[row.status] || ""}`}
                      data-testid={`addon-status-${row.site_id}`}>
                      {row.status === "past_due" ? "payment issue" : row.status}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    {(() => {
                      const a = attr[row.site_id];
                      if (!a) return <span className="text-muted-foreground">—</span>;
                      if (a.included_with_complete) {
                        return <span className="text-xs text-muted-foreground" data-testid={`attr-included-${row.site_id}`}>
                          included with Complete
                        </span>;
                      }
                      const on = a.status === "active";
                      return (
                        <Button size="sm" variant={on ? "ghost" : "outline"} disabled={busy === "a" + row.site_id}
                          data-testid={`attr-toggle-${row.site_id}`} onClick={() => toggleAttr(row, !on)}>
                          {on ? "On · turn off" : "Switch on"}
                        </Button>
                      );
                    })()}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex justify-end gap-1.5">
                      <Button size="sm" variant="outline" data-testid={`comp-${row.site_id}`}
                        onClick={() => setComp({
                          row, tier: row.tier_key || "reviews",
                          source: row.source === "bundle" ? "bundle" : "manual_comp",
                          bundle_name: row.bundle_name || "", reason: "", ends_at: row.ends_at || "",
                        })}>
                        <Gift className="mr-1.5 h-3.5 w-3.5" /> {row.status === "inactive" ? "Comp" : "Change"}
                      </Button>
                      {row.status === "paused" && (
                        <Button size="sm" variant="ghost" disabled={busy === "r" + row.site_id}
                          onClick={() => resume(row)} data-testid={`resume-${row.site_id}`}>
                          <PlayCircle className="mr-1.5 h-3.5 w-3.5" /> Resume
                        </Button>
                      )}
                      {["active", "past_due"].includes(row.status) && (
                        <Button size="sm" variant="ghost" onClick={() => setEnd({ row, confirm: "", reason: "" })}
                          data-testid={`end-${row.site_id}`}>
                          <PauseCircle className="mr-1.5 h-3.5 w-3.5" /> Turn off
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* comp / change */}
      <Dialog open={!!comp} onOpenChange={(o) => !o && setComp(null)}>
        <DialogContent data-testid="comp-dialog">
          <DialogHeader>
            <DialogTitle>Comp {comp?.row?.business_name || comp?.row?.url}</DialogTitle>
            <DialogDescription>
              No Stripe involved — the site goes live on this tier straight away and the change is logged.
            </DialogDescription>
          </DialogHeader>
          {comp && (
            <div className="space-y-4">
              <div className="space-y-1">
                <Label>Tier</Label>
                <Select value={comp.tier} onValueChange={(v) => setComp({ ...comp, tier: v })}>
                  <SelectTrigger data-testid="comp-tier"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {tiers.map((t) => (
                      <SelectItem key={t.key} value={t.key}>{t.label} (${t.monthly}/mo)</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label>Why</Label>
                <Select value={comp.source} onValueChange={(v) => setComp({ ...comp, source: v })}>
                  <SelectTrigger data-testid="comp-source"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="manual_comp">Courtesy / internal</SelectItem>
                    <SelectItem value="bundle">Included in a package the client pays for</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {comp.source === "bundle" && (
                <div className="space-y-1">
                  <Label htmlFor="bundle">Package name</Label>
                  <Input id="bundle" value={comp.bundle_name} data-testid="comp-bundle-name"
                    placeholder="Business-in-a-Box Elite"
                    onChange={(e) => setComp({ ...comp, bundle_name: e.target.value })} />
                </div>
              )}
              <div className="space-y-1">
                <Label htmlFor="reason">Note for the log</Label>
                <Input id="reason" value={comp.reason} data-testid="comp-reason"
                  placeholder="Grandfathered, billed outside Beacon"
                  onChange={(e) => setComp({ ...comp, reason: e.target.value })} />
              </div>
              <div className="space-y-1">
                <Label htmlFor="ends">End date (optional)</Label>
                <Input id="ends" type="date" value={(comp.ends_at || "").slice(0, 10)} data-testid="comp-ends-at"
                  onChange={(e) => setComp({ ...comp, ends_at: e.target.value })} />
                <p className="text-xs text-muted-foreground">
                  Leave blank for no end date. On expiry it pauses for 30 days before anything disconnects.
                </p>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setComp(null)}>Cancel</Button>
            <Button onClick={saveComp} disabled={busy === "comp"} data-testid="comp-save">
              {busy === "comp" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Gift className="mr-2 h-4 w-4" />}
              Save comp
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* turn off — typed confirmation */}
      <Dialog open={!!end} onOpenChange={(o) => !o && setEnd(null)}>
        <DialogContent data-testid="end-dialog">
          <DialogHeader>
            <DialogTitle>Turn off this add-on?</DialogTitle>
            <DialogDescription>
              This stops posting and syncing for {end?.row?.business_name || end?.row?.url} and, after 30 days,
              disconnects Facebook, Instagram, Google Business and LinkedIn for this site. Nothing is
              disconnected today.
            </DialogDescription>
          </DialogHeader>
          {end && (
            <div className="space-y-4">
              <div className="space-y-1">
                <Label htmlFor="confirmName">
                  Type <span className="font-mono text-foreground">{end.row.business_name || end.row.url}</span> to confirm
                </Label>
                <Input id="confirmName" value={end.confirm} data-testid="end-confirm"
                  autoComplete="off" onChange={(e) => setEnd({ ...end, confirm: e.target.value })} />
              </div>
              <div className="space-y-1">
                <Label htmlFor="endReason">Note for the log</Label>
                <Input id="endReason" value={end.reason} data-testid="end-reason"
                  placeholder="Client cancelled the package"
                  onChange={(e) => setEnd({ ...end, reason: e.target.value })} />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEnd(null)}>Keep it on</Button>
            <Button variant="destructive" onClick={confirmEnd} disabled={busy === "end"} data-testid="end-confirm-button">
              {busy === "end" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <PauseCircle className="mr-2 h-4 w-4" />}
              Pause for 30 days
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AppLayout>
  );
}
